/* eslint-disable */
import { useMemo, useState, useEffect, SetStateAction, Dispatch } from 'react'
import { Text, Box, Card, Flex, Skeleton, Button, useMatchBreakpoints } from '@noahswap/uikit'
import LineChart from 'views/Info/components/InfoCharts/LineChart'
import BarChart from 'views/Info/components/InfoCharts/BarChart'
import { TabToggleGroup, TabToggle } from 'components/TabToggle'
import { useTranslation } from '@noahswap/localization'
import { formatAmount, formatPrice, formatNumber } from 'utils/formatInfoNumbers'
import { ChartEntry, TokenData, PriceChartEntry } from 'state/info/types'
import { fromUnixTime } from 'date-fns'
import dynamic from 'next/dynamic'
import styled from 'styled-components'

const CandleChart = dynamic(() => import('views/Info/components/InfoCharts/CandleChart'), {
  ssr: false,
})
const KlineChart = dynamic(() => import('views/Info/components/InfoCharts/KlineChart'), {
  ssr: false,
})

const StyledButton = styled(Button)`
  padding: 10px 12px;
  font-weight: 400;
  margin-right: 10px;
  font-size: 14px;
  color: #000;
  opacity: 1 !important;
`

const FlexRow = styled.div<{ active: boolean; isMobile?: boolean }>`
  cursor: pointer;
  background: ${({ active }) => (active ? '#FAD436' : '#fff')};
  border-width: 1px;
  border-style: solid;
  font-size: 14px;
  padding: ${({ isMobile }) => (isMobile ? '5px 5px' : '5px 10px')};
  /* padding: 5px 10px; */
  border-radius: 6px;
  border-color: ${({ active }) => (active ? '#14162A' : '#fff')};
  margin-right: 10px;
`

enum ChartView {
  LIQUIDITY,
  VOLUME,
  PRICE,
}

interface ChartCardProps {
  variant: 'pool' | 'token'
  chartData: ChartEntry[]
  tokenData?: TokenData
  tokenPriceData?: PriceChartEntry[]
  showKline?: boolean
  KLineTime?: string
  pairId?: string
  poolData?: any
  setKLineTime?: Dispatch<SetStateAction<string>>
}

const ChartCard: React.FC<React.PropsWithChildren<ChartCardProps>> = ({
  variant,
  chartData,
  tokenData,
  tokenPriceData,
  showKline,
  KLineTime,
  pairId,
  poolData,
  setKLineTime,
}) => {
  const { isDesktop } = useMatchBreakpoints()
  const isMobile = isDesktop === false
  const [view, setView] = useState(ChartView.VOLUME)
  const [hoverValue, setHoverValue] = useState<number | undefined>(
    chartData?.length ? formatAmount(chartData[chartData.length - 1][view]) : 0,
  )
  // const [hoverValue, setHoverValue] = useState<number | undefined>(
  //   view !== ChartView.PRICE ? (chartData?.length ? formatAmount(chartData[chartData.length - 1][view]) : 0) :
  //     tokenPriceData?.length ? formatAmount(tokenPriceData[tokenPriceData.length - 1].open) : 0
  // )
  const [hoverDate, setHoverDate] = useState<string | undefined>()
  const [kLoading, setKLoading] = useState(false)
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const [loading, setLoading] = useState(false)

  const currentDate = new Date().toLocaleString(locale, { month: 'short', year: 'numeric', day: 'numeric' })

  const formattedTvlData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: fromUnixTime(day.date),
          value: day.liquidityUSD,
        }
      })
    }
    return []
  }, [chartData])
  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: fromUnixTime(day.date),
          value: day.volumeUSD,
        }
      })
    }
    return []
  }, [chartData])

  useEffect(() => {
    setLoading(true)
    if (chartData?.length) {
      setLoading(false)
    } else {
      setTimeout(() => {
        setLoading(false)
      }, 500)
    }
  }, [chartData])

  useEffect(() => {
    setKLoading(true)
    setTimeout(() => {
      setKLoading(false)
    }, 500)
  }, [tokenPriceData])

  const getLatestValueDisplay = () => {
    let valueToDisplay = null
    // const { t } = useTranslation()
    if (hoverValue) {
      if (view === ChartView.PRICE) {
        valueToDisplay = formatNumber(formatPrice(hoverValue), hoverValue)
      } else {
        valueToDisplay = formatAmount(Number(hoverValue) || 0)
      }
    } else if (hoverValue === 0) {
      valueToDisplay = 0
    } else if (view === ChartView.VOLUME && formattedVolumeData.length > 0) {
      valueToDisplay = formatAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value)
    } else if (view === ChartView.LIQUIDITY && formattedTvlData.length > 0) {
      valueToDisplay = formatAmount(formattedTvlData[formattedTvlData.length - 1]?.value)
    } else if (view === ChartView.PRICE && tokenPriceData.length) {
      valueToDisplay = formatNumber(
        formatPrice(tokenPriceData[tokenPriceData.length - 1]?.open),
        tokenPriceData[tokenPriceData.length - 1]?.open,
      )
    }

    // console.log(valueToDisplay, 'valueToDisplay')

    return valueToDisplay ? (
      <Text fontSize="24px" bold>
        {view === ChartView.PRICE ? ' ' : '$'}
        {valueToDisplay}
      </Text>
    ) : (
      <Text fontSize="24px" bold>
        {view === ChartView.PRICE ? ' ' : '$'}0
      </Text>
      // <Skeleton height="36px" width="128px" />
    )
  }

  useEffect(() => {
    // console.log(tokenPriceData[tokenPriceData.length - 1]?.open, '11111111111111')
    if (view === ChartView.PRICE) {
      setHoverValue(
        formatNumber(
          formatPrice(tokenPriceData[tokenPriceData.length - 1]?.open),
          tokenPriceData[tokenPriceData.length - 1]?.open,
        ),
      )
    } else if (view === ChartView.VOLUME && formattedVolumeData.length > 0) {
      setHoverValue(formatAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value))
    } else if (view === ChartView.LIQUIDITY && formattedTvlData.length > 0) {
      setHoverValue(formatAmount(formattedTvlData[formattedTvlData.length - 1]?.value))
    }
  }, [view, tokenPriceData])

  useEffect(() => {
    if (showKline) {
      setView(ChartView.PRICE)
    }
  }, [showKline])

  useEffect(() => {
    if (view === ChartView.PRICE && !hoverValue) {
      setHoverValue(
        formatNumber(
          formatPrice(tokenPriceData[tokenPriceData.length - 1]?.open),
          tokenPriceData[tokenPriceData.length - 1]?.open,
        ),
      )
    }
  }, [hoverValue, view])

  const handleTab = (id) => {
    setKLineTime(id)
  }
  const tabArr = [
    {
      id: 6,
      label: t('noah480'),
      value: 'minute5',
    },
    {
      id: 5,
      label: t('noah215'),
      value: 'minute15',
    },
    {
      id: 4,
      label: t('noah214'),
      value: 'hour',
    },
    {
      id: 0,
      label: t('noah197'),
      value: 'hour4',
    },
    {
      id: 1,
      label: t('noah198'),
      value: 'day',
    },
    {
      id: 2,
      label: t('noah199'),
      value: 'week',
    },
    {
      id: 3,
      label: t('noah200'),
      value: 'month',
    },
  ]

  return (
    <Card style={{ padding: 0, background: '#fff', height: isMobile ? '440px' : 'auto' }}>
      {/* <TabToggleGroup>
        <TabToggle isActive={view === ChartView.VOLUME} onClick={() => setView(ChartView.VOLUME)}>
          <Text>{t('noah107')}</Text>
        </TabToggle>
        <TabToggle isActive={view === ChartView.LIQUIDITY} onClick={() => setView(ChartView.LIQUIDITY)}>
          <Text>{t('noah37')}</Text>
        </TabToggle>
        {variant === 'token' && (
          <TabToggle isActive={view === ChartView.PRICE} onClick={() => setView(ChartView.PRICE)}>
            <Text>{t('noah81')}</Text>
          </TabToggle>
        )}
      </TabToggleGroup> */}

      <Flex style={{ padding: isMobile ? '25px 16px 0' : '25px 30px 0' }}>
        {showKline && (
          <StyledButton
            height="32px"
            variant={view === ChartView.PRICE ? 'primary' : 'text'}
            onClick={() => setView(ChartView.PRICE)}
          >
            {t('noah81')}
          </StyledButton>
        )}
        <StyledButton
          height="32px"
          variant={view === ChartView.VOLUME ? 'primary' : 'text'}
          onClick={() => setView(ChartView.VOLUME)}
        >
          {/* 交易额 */}
          {t('noah107')}
        </StyledButton>
        <StyledButton
          height="32px"
          variant={view === ChartView.LIQUIDITY ? 'primary' : 'text'}
          onClick={() => setView(ChartView.LIQUIDITY)}
        >
          {/* 流动性 */}
          {t('noah37')}
        </StyledButton>
      </Flex>
      {!isMobile ? (
        <Flex justifyContent="space-between" alignItems="center">
          {view !== ChartView.PRICE && (
            <Flex flexDirection="column" px="24px" pt="24px">
              {hoverValue ? (
                <Text fontSize="24px" bold>
                  {/* {view === ChartView.PRICE ? ' ' : '$'} */}${hoverValue}
                </Text>
              ) : (
                <Text fontSize="24px" bold>
                  $0
                </Text>
              )}
              <Text small color="#000">
                {hoverDate || currentDate}
              </Text>
            </Flex>
          )}
          {/* <Flex px="24px" flexWrap="wrap">
            {view === ChartView.PRICE &&
              tabArr.map((v) => {
                return (
                  <FlexRow
                    key={v.id}
                    active={v.value === KLineTime}
                    isMobile={isMobile}
                    onClick={() => handleTab(v.value)}
                  >
                    {v.label}
                  </FlexRow>
                )
              })}
          </Flex> */}
        </Flex>
      ) : (
        <Box style={{ padding: '10px 16px' }}>
          {view !== ChartView.PRICE && (
            <Flex flexDirection="column" pt="10px">
              {hoverValue ? (
                <Text fontSize="24px" bold>
                  {/* {view === ChartView.PRICE ? ' ' : '$'} */}${hoverValue}
                </Text>
              ) : (
                <Text fontSize="24px" bold>
                  {/* {view === ChartView.PRICE ? ' ' : '$'}0 */}
                  $0
                </Text>
              )}
              <Text small color="#000">
                {hoverDate || currentDate}
              </Text>
            </Flex>
          )}
          {/* <Flex style={{ marginTop: '10px' }} flexWrap="wrap">
            {view === ChartView.PRICE &&
              tabArr.map((v) => {
                return (
                  <FlexRow
                    isMobile={isMobile}
                    key={v.id}
                    active={v.value === KLineTime}
                    onClick={() => handleTab(v.value)}
                    style={{ marginRight: v.id === 3 ? '0px' : '10px' }}
                  >
                    {v.label}
                  </FlexRow>
                )
              })}
          </Flex> */}
        </Box>
      )}

      {/* height={variant === 'token' ? '250px' : '335px'} */}
      <Box
        px={!isMobile ? '24px' : '10px'}
        height={
          view === ChartView.PRICE
            ? !isMobile
              ? '500px'
              : '300px'
            : !isMobile
            ? 'calc(500px - 61px)'
            : 'calc(360px - 61px)'
        }
        style={{ marginTop: view === ChartView.PRICE ? (!isMobile ? '20px' : '0') : '0' }}
      >
        {view === ChartView.LIQUIDITY ? (
          <LineChart
            data={formattedTvlData}
            setHoverValue={setHoverValue}
            setHoverDate={setHoverDate}
            loading={loading}
          />
        ) : view === ChartView.VOLUME ? (
          <BarChart
            data={formattedVolumeData}
            setHoverValue={setHoverValue}
            setHoverDate={setHoverDate}
            loading={loading}
          />
        ) : view === ChartView.PRICE ? (
          // <CandleChart
          //   data={tokenPriceData}
          //   setValue={setHoverValue}
          //   setLabel={setHoverDate}
          //   KLineTime={KLineTime}
          //   kLoading={kLoading}
          // />
          <KlineChart
            data={tokenPriceData}
            setValue={setHoverValue}
            setLabel={setHoverDate}
            poolData={poolData}
            // kLoading={kLoading}
          />
        ) : null}
      </Box>
      {/* <KlineChart
        data={tokenPriceData}
        // setValue={setHoverValue}
        // setLabel={setHoverDate}
        KLineTime={KLineTime}
        pairId={pairId}
        poolData={poolData}
        // kLoading={kLoading}
      /> */}
    </Card>
  )
}

export default ChartCard
